import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istNetzFehler, NETZ_FEHLER_TEXT } from '../netzFehler';
import { mitZeitlimit } from '../ablageHilfen';
import { angemeldeterBenutzer } from '../angemeldeterBenutzer';
import { vorankInhaltsHash } from '../vorankuendigungAblage';
import { EIGENE_UNTERLAGEN_KATEGORIE, ORDNER_JE_MODUL } from '../unterlagenAblage';
import {
    AblageFehler,
    entferneDatei,
    findeErzeugteUnterlage,
    istDoppelablage,
    istFehlendeKennzeichnung,
    ladeDokumentHerunter,
    uploadDokument,
} from '../projektDokumenteService';
import { pruefeDatei } from '../../components/dokumente/dokumenteLogic';
import type { DokumentEntwurf } from '../../components/dokumente/types';
import type { VorankuendigungData } from '../../types';
import { istKalenderTag, istUnterschriftsBild } from './vorankAnfragePruefung';
import { VORANK_TOKEN_MUSTER } from './vorankUnterschriftTypes';
import type {
    VorankFassungAngelegt,
    VorankFassungZeile,
    VorankKartenStatus,
    VorankKorrektur,
    VorankUnterschrift,
} from './vorankUnterschriftTypes';

/**
 * Der ANGEMELDETE Teil von „Vorankündigung unterschreiben"
 * (docs/vorankuendigung-unterschrift-plan.md §4, §7, §8): Fassung anlegen,
 * Fassungen lesen, Papierweg eintragen, unterschriebene PDF nachtragen und
 * die Dateien dazu in „Dokumente" ablegen.
 *
 * Fassungen, Token und Unterschriften liegen in `vorank_fassungen`
 * (authenticated liest, schreibt nur über RPCs) — NIE im Projekt-Blob, den
 * der Autosave zurückschreibt.
 *
 * ═══ FAIL-OPEN BEIM ANLEGEN, LAUT BEIM EINTRAGEN ═════════════════════════
 * Der Unterschrift-Link ist eine BEIGABE zum PDF-Export. `legeFassungAn` und
 * `ladeFassungen` geben bei JEDEM Fehler `null` (fehlende Migration 42P01/
 * PGRST202/PGRST205, RLS 42501, kein Netz, Zeitbudget) — der Export läuft
 * dann ohne QR, die Statuskarte bleibt weg. Muster `bereiteFreimeldungLinkVor`.
 *
 * Die Gegenrichtung gilt für `papierEingang`, `signiertSetzen`,
 * `legeVorankDateiAb` und `ladeDokumentBytes`: eine eingelesene Unterschrift
 * darf nicht still verschwinden, sie wirft mit deutschem Klartext.
 *
 * ═══ ABLAGE OHNE AUSGABE-SEMANTIK ═════════════════════════════════════════
 * `legeVorankDateiAb` geht bewusst NICHT über `legeUnterlageAb`: der Kern
 * kennt nur die Anlässe freigabe/versand/entwurf (Schlagwort, Titelpräfix,
 * Entwurfssatz) und befördert eine vorgefundene Entwurfszeile. Eine
 * unterschriebene Fassung ist keines davon. Hier entsteht nur EINE NEUE ZEILE
 * über `uploadDokument` (Muster `legeErzeugtAb` der SiFa-GBU) — kein Journal,
 * kein Steckbrief-Anker, keine Umbeschriftung.
 */

const T_FASSUNGEN = 'vorank_fassungen';
const T_DOKUMENTE = 'project_documents';

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => {
    if (v == null) return null;
    const s = str(v).trim();
    return s ? s : null;
};

/** Erster gesetzter Wert unter mehreren Schlüsseln — die Quelle darf snake ODER camel liefern. */
const pick = (row: Zeile, ...keys: string[]): unknown => {
    for (const k of keys) if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
    return undefined;
};

/** jsonb als Objekt; Zeichenketten werden geparst, ein Array liefert seine erste Zeile. */
const alsObjekt = (data: unknown): Zeile | null => {
    if (data == null) return null;
    if (Array.isArray(data)) return data.length > 0 ? alsObjekt(data[0]) : null;
    if (typeof data === 'string') {
        try {
            return alsObjekt(JSON.parse(data));
        } catch {
            return null;
        }
    }
    return typeof data === 'object' ? (data as Zeile) : null;
};

/** Der eingefrorene Inhalt: nur ein echtes Objekt (auch als JSON-Text), nie eine Liste. */
const alsInhalt = (v: unknown): VorankuendigungData | null => {
    let wert = v;
    if (typeof v === 'string') {
        try {
            wert = JSON.parse(v);
        } catch {
            return null;
        }
    }
    return wert !== null && typeof wert === 'object' && !Array.isArray(wert)
        ? (wert as VorankuendigungData)
        : null;
};

const errorCode = (e: unknown): string =>
    typeof e === 'object' && e !== null ? str((e as { code?: unknown }).code) : '';

const errorMessage = (e: unknown): string =>
    typeof e === 'object' && e !== null ? str((e as { message?: unknown }).message) : '';

/** Tabelle oder Funktion fehlt (Nr. 186/187 nicht gefahren): je nach Schema-Cache einer dieser Codes. */
const istFehlendesSchema = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01' || code === 'PGRST202' || code === '42883';
};

const istGespeichertesProjekt = (projectId: string): boolean => !!projectId && projectId !== 'temp-id';

// ─── Inhalt und Kennung ─────────────────────────────────────────────────────

/**
 * Der Inhalt einer Fassung: eine Kopie OHNE Archiv (alle Vorgänger) und
 * Verlauf (Export-Zeitpunkte). Beides steht nicht auf dem Blatt, bläht den
 * Schnappschuss auf und ginge sonst an die öffentliche Seite. Rein.
 */
export const fassungsInhalt = (data: VorankuendigungData): VorankuendigungData => {
    const kopie: VorankuendigungData = { ...data };
    delete kopie.archive;
    delete kopie.history;
    return kopie;
};

/**
 * Kennung des Inhalts einer Fassung — `vorankInhaltsHash` über
 * `fassungsInhalt`. Der lässt Archiv, Verlauf und die Sperrmarken der Punkte
 * 7–10 ohnehin weg; derselbe Inhalt ergibt also mit und ohne Archiv denselben
 * Hash. Gleicher Hash = gleiche Fassung = gleicher Token (`vorank_fassung_anlegen`).
 */
export const vorankHash = (data: VorankuendigungData): string => vorankInhaltsHash(fassungsInhalt(data));

// ─── Fassung anlegen (Export) ───────────────────────────────────────────────

/**
 * Wie lange der Export höchstens auf den Link wartet — wie
 * `FREIMELDUNG_ZEITBUDGET_MS`: lang genug für einen kalten Schema-Cache,
 * kurz genug, dass niemand glaubt, die App sei stehen geblieben.
 */
export const VORANK_FASSUNG_ZEITBUDGET_MS = 8_000;

/** Antwort von `vorank_fassung_anlegen` → `VorankFassungAngelegt`; ein Token in falscher Form ergäbe einen kaputten QR → `null`. */
const angelegtAusRpc = (raw: unknown): VorankFassungAngelegt | null => {
    const row = alsObjekt(raw);
    if (!row) return null;
    const fassungId = strOrNull(pick(row, 'fassungId', 'fassung_id', 'id'));
    const token = str(pick(row, 'token')).trim();
    if (!fassungId || !VORANK_TOKEN_MUSTER.test(token)) return null;
    return {
        fassungId,
        token,
        gueltigBis: str(pick(row, 'gueltigBis', 'gueltig_bis')),
        neu: pick(row, 'neu') === true,
        unterschrieben: pick(row, 'unterschrieben') === true,
        fassungVom: str(pick(row, 'fassungVom', 'created_at')),
    };
};

/**
 * Legt die Fassung zum Export an (RPC `vorank_fassung_anlegen`) — oder holt
 * die vorhandene mit gleichem Hash (gleicher Token, `neu: false`).
 *
 * WIRFT NIE: `null` bei jedem Fehler, bei ungespeichertem Projekt, bei einer
 * unlesbaren Antwort und nach `VORANK_FASSUNG_ZEITBUDGET_MS`. Der Export geht
 * dann ohne QR-Kasten hinaus. Die späte Antwort läuft im Hintergrund zu Ende
 * — abbrechen lässt sich ein PostgREST-Aufruf nicht; legt sie die Fassung
 * doch noch an, findet der nächste Export sie über den Hash wieder.
 */
export const legeFassungAn = async (a: {
    projectId: string;
    inhalt: VorankuendigungData;
    seiten: number;
    archivEintragId: string | null;
}): Promise<VorankFassungAngelegt | null> => {
    try {
        const pid = (a?.projectId || '').trim();
        if (!istGespeichertesProjekt(pid) || !a.inhalt) return null;
        if (!Number.isFinite(a.seiten) || a.seiten < 1) {
            console.warn('Vorankündigung: Seitenzahl unbrauchbar — keine Fassung für den Unterschrift-Link.', a.seiten);
            return null;
        }
        const arbeit = (async (): Promise<VorankFassungAngelegt | null> => {
            const { data, error } = await supabase.rpc('vorank_fassung_anlegen', {
                p_project_id: pid,
                p_inhalt: fassungsInhalt(a.inhalt),
                p_hash: vorankHash(a.inhalt),
                p_seiten: Math.round(a.seiten),
                p_archiv_id: (a.archivEintragId || '').trim() || null,
            });
            if (error) throw error;
            const angelegt = angelegtAusRpc(data);
            if (!angelegt) console.warn('Vorankündigung: Antwort von vorank_fassung_anlegen unlesbar', data);
            return angelegt;
        })();
        return await mitZeitlimit(arbeit, VORANK_FASSUNG_ZEITBUDGET_MS);
    } catch (e) {
        const grund = istFehlendesSchema(e) ? 'Migration Nr. 186/187 fehlt' : describeError(e);
        console.warn(`Vorankündigung: Fassung für den Unterschrift-Link nicht angelegt (${grund}) — die PDF geht ohne QR.`);
        return null;
    }
};

// ─── Fassungen lesen ────────────────────────────────────────────────────────

const ganzzahl = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

const unterschriftAusZeile = (row: Zeile): VorankUnterschrift | null => {
    const weg = str(pick(row, 'weg'));
    const am = strOrNull(pick(row, 'unterschriebenAm', 'unterschrieben_am'));
    if ((weg !== 'digital' && weg !== 'papier') || !am) return null;
    const digital = weg === 'digital';
    const bild = pick(row, 'unterschriftBild', 'unterschrift_bild');
    const art = str(pick(row, 'unterschriftArt', 'unterschrift_art'));
    const datum = pick(row, 'unterschriftDatum', 'unterschrift_datum');
    return {
        weg,
        am,
        name: strOrNull(pick(row, 'unterschriftName', 'unterschrift_name')),
        funktion: strOrNull(pick(row, 'unterschriftFunktion', 'unterschrift_funktion')),
        // Nur ein PNG im Rahmen der RPC — sonst landete es ungeprüft im PDF.
        bild: digital && istUnterschriftsBild(bild) ? bild : null,
        art: digital && (art === 'gezeichnet' || art === 'bild') ? art : null,
        // E6: eingetragener Kalendertag (Spalte `date` → „YYYY-MM-DD“), nur digital.
        datum: digital && istKalenderTag(datum) ? datum : null,
    };
};

const korrekturAusZeile = (row: Zeile): VorankKorrektur | null => {
    const text = strOrNull(pick(row, 'korrekturText', 'korrektur_text'));
    if (!text) return null;
    return {
        am: str(pick(row, 'korrekturAm', 'korrektur_am')),
        name: strOrNull(pick(row, 'korrekturName', 'korrektur_name')),
        text,
    };
};

/**
 * Eine Zeile aus `vorank_fassungen` → `VorankFassungZeile`, oder `null`.
 * Rein und robust: snake ODER camel; ohne Id oder ohne lesbaren Inhalt gibt
 * es keine Fassung (eine Fassung ohne Inhalt lässt sich weder anzeigen noch
 * plotten). Unbekannter Weg oder fehlender Zeitpunkt → nicht unterschrieben;
 * ein Bild, das kein PNG im Rahmen der RPC ist, → `null`. `seiten` 0 heißt
 * „unbekannt".
 */
export const zeileAusDb = (row: unknown): VorankFassungZeile | null => {
    const r = row !== null && typeof row === 'object' && !Array.isArray(row) ? (row as Zeile) : null;
    if (!r) return null;
    const id = strOrNull(pick(r, 'id'));
    const inhalt = alsInhalt(pick(r, 'inhalt'));
    if (!id || !inhalt) return null;
    return {
        id,
        projectId: str(pick(r, 'projectId', 'project_id')),
        inhalt,
        inhaltHash: str(pick(r, 'inhaltHash', 'inhalt_hash')).trim(),
        seiten: ganzzahl(pick(r, 'seiten')),
        archivEintragId: strOrNull(pick(r, 'archivEintragId', 'archiv_eintrag_id')),
        token: str(pick(r, 'token')).trim(),
        gueltigBis: str(pick(r, 'gueltigBis', 'gueltig_bis')),
        createdAt: str(pick(r, 'createdAt', 'created_at')),
        createdBy: strOrNull(pick(r, 'createdBy', 'created_by')),
        unterschrift: unterschriftAusZeile(r),
        scanDokumentId: strOrNull(pick(r, 'scanDokumentId', 'scan_dokument_id')),
        signiertDokumentId: strOrNull(pick(r, 'signiertDokumentId', 'signiert_dokument_id')),
        korrektur: korrekturAusZeile(r),
        ersetztAm: strOrNull(pick(r, 'ersetztAm', 'ersetzt_am')),
        mailGesendetAm: strOrNull(pick(r, 'mailGesendetAm', 'mail_gesendet_am')),
        mailFehler: strOrNull(pick(r, 'mailFehler', 'mail_fehler')),
    };
};

/** Zeitpunkt in ms; unlesbar oder leer zählt als ältester. */
const zeitwert = (iso: string | null | undefined): number => {
    const ms = Date.parse(iso || '');
    return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
};

/** Sortierschlüssel „späterer zuerst": negativ, wenn `a` später ist. */
const spaeterZuerst = (a: string | null | undefined, b: string | null | undefined): number => {
    const za = zeitwert(a);
    const zb = zeitwert(b);
    return za === zb ? 0 : za > zb ? -1 : 1;
};

/** Neueste Fassung zuerst; bei gleichem Zeitpunkt die größere Id zuerst (wie die Abfrage). */
const neuesteZuerst = (a: VorankFassungZeile, b: VorankFassungZeile): number =>
    spaeterZuerst(a.createdAt, b.createdAt) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);

/** Seitengröße beim Lesen — PostgREST kappt ungepagte Antworten still (Lehre Sendeverlauf). */
const SEITE = 1000;

/**
 * Alle Fassungen eines Projekts, neueste zuerst. `null`, wenn die Tabelle
 * fehlt (Nr. 186/187 nicht gefahren) oder irgendein Fehler auftritt — die
 * Statuskarte bleibt dann weg. Ungespeichertes Projekt → `[]`. Wirft nie.
 */
export const ladeFassungen = async (projectId: string): Promise<VorankFassungZeile[] | null> => {
    try {
        const pid = (projectId || '').trim();
        if (!istGespeichertesProjekt(pid)) return [];
        const alle: VorankFassungZeile[] = [];
        for (let von = 0; ; von += SEITE) {
            const { data, error } = await supabase
                .from(T_FASSUNGEN)
                .select('*')
                .eq('project_id', pid)
                .order('created_at', { ascending: false })
                .order('id', { ascending: false })
                .range(von, von + SEITE - 1);
            if (error) {
                if (!istFehlendesSchema(error)) console.warn('Vorankündigung: Fassungen nicht lesbar', describeError(error));
                return null;
            }
            const seite = Array.isArray(data) ? data : [];
            for (const roh of seite) {
                const zeile = zeileAusDb(roh);
                if (zeile) alle.push(zeile);
            }
            if (seite.length < SEITE) break;
        }
        return alle.sort(neuesteZuerst);
    } catch (e) {
        console.warn('Vorankündigung: Fassungen nicht lesbar', describeError(e));
        return null;
    }
};

// ─── Abgeleiteter Zustand ───────────────────────────────────────────────────

/**
 * Die jüngste unterschriebene Fassung — mit `hash` nur eine mit genau diesem
 * Inhalt (ein leerer Hash passt nie). „Jüngste" nach Anlage der Fassung, bei
 * Gleichstand nach dem Zeitpunkt der Unterschrift. Rein.
 */
export const juengsteUnterschriebene = (
    fassungen: readonly VorankFassungZeile[] | null,
    hash?: string,
): VorankFassungZeile | null => {
    const mitHash = hash !== undefined;
    const gesucht = (hash || '').trim();
    if (mitHash && !gesucht) return null;
    const kandidaten = (fassungen || []).filter(f =>
        !!f?.unterschrift && (!mitHash || f.inhaltHash === gesucht));
    if (!kandidaten.length) return null;
    return [...kandidaten].sort((a, b) =>
        spaeterZuerst(a.createdAt, b.createdAt)
        || spaeterZuerst(a.unterschrift?.am, b.unterschrift?.am)
        || neuesteZuerst(a, b))[0];
};

/**
 * Zustand der Statuskarte. Rein. Reihenfolge der Regeln:
 *  1. keine Fassung (leer oder `null`) → `keine_fassung`;
 *  2. eine unterschriebene Fassung mit dem aktuellen Inhalt → `unterschrieben`;
 *  3. die jüngste Fassung hat einen Korrekturwunsch und ist weder
 *     unterschrieben noch ersetzt → `korrektur`;
 *  4. irgendeine Fassung ist unterschrieben (nur eben nicht der aktuelle
 *     Stand) → `unterschrieben_veraltet`;
 *  5. sonst → `offen`.
 */
export const vorankKartenStatus = (
    fassungen: readonly VorankFassungZeile[] | null,
    aktuellerHash: string,
): VorankKartenStatus => {
    const liste = (fassungen || []).filter(Boolean);
    if (!liste.length) return 'keine_fassung';
    if (juengsteUnterschriebene(liste, aktuellerHash || '')) return 'unterschrieben';
    const juengste = [...liste].sort(neuesteZuerst)[0];
    if (juengste.korrektur && !juengste.unterschrift && !juengste.ersetztAm) return 'korrektur';
    if (liste.some(f => !!f.unterschrift)) return 'unterschrieben_veraltet';
    return 'offen';
};

// ─── Papierweg und Nachtrag der unterschriebenen PDF ────────────────────────

/** Der Text nach `status:` einer P0001-Meldung (`status:unterschrieben` …), sonst leer. */
const zustandAusMeldung = (e: unknown): string => {
    const treffer = /status:\s*([a-z_]+)/i.exec(errorMessage(e));
    return treffer ? treffer[1].toLowerCase() : '';
};

const ZUSTAND_TEXT: Record<string, string> = {
    unterschrieben: 'Diese Fassung ist bereits unterschrieben — digital über den Link oder schon eingelesen.',
    offen: 'Diese Fassung ist noch nicht unterschrieben.',
    ersetzt: 'Diese Fassung ist durch eine neuere ersetzt.',
    abgelaufen: 'Der Link dieser Fassung ist abgelaufen.',
};

/**
 * Deutscher Klartext zu einem Fehler der RPCs `vorank_papier_eingang` und
 * `vorank_signiert_setzen` — für die Bearbeiteransicht, deshalb mit der
 * Meldung des Servers, wo sie weiterhilft. Rein.
 */
export const vorankRpcFehlertext = (e: unknown): string => {
    const code = errorCode(e);
    if (istFehlendesSchema(e)) {
        return 'Die Datenbank kennt die Unterschrift der Vorankündigung noch nicht (Migration Nr. 186/187 fehlt). '
            + 'Bitte an die Verwaltung wenden.';
    }
    if (code === 'P0002') return 'Diese Fassung der Vorankündigung gibt es nicht (mehr). Bitte die Ansicht neu laden.';
    if (code === 'P0001') {
        const satz = ZUSTAND_TEXT[zustandAusMeldung(e)] || 'Der Zustand der Fassung lässt das gerade nicht zu.';
        return `${satz} Bitte die Ansicht neu laden.`;
    }
    if (code === '22023') {
        const grund = errorMessage(e).trim();
        return `Die Angaben wurden abgelehnt${grund ? `: ${grund}` : '.'}`;
    }
    if (code === 'P0004') return 'Die Grenze für diese Fassung ist erreicht — es wird nichts mehr angenommen.';
    if (code === '42501') return 'Für dieses Projekt fehlt die Berechtigung — bitte an die Verwaltung wenden.';
    if (istNetzFehler(e)) return NETZ_FEHLER_TEXT;
    return describeError(e);
};

const werfe = (e: unknown): never => {
    throw new Error(vorankRpcFehlertext(e));
};

const bereitsAus = (data: unknown): { bereits: boolean } => ({ bereits: alsObjekt(data)?.bereits === true });

/**
 * Trägt den Papierweg ein (RPC `vorank_papier_eingang`): `weg = 'papier'`,
 * Original und normalisierte A4-PDF. Beide Dokumente müssen schon in
 * „Dokumente" liegen (`legeVorankDateiAb`) und zum selben Projekt gehören —
 * das prüft die RPC. Ein zweiter Lauf derselben Eintragung: `{ bereits: true }`.
 * Wirft `Error` mit deutschem Klartext.
 */
export const papierEingang = async (a: {
    fassungId: string;
    scanDokumentId: string;
    signiertDokumentId: string;
    name: string | null;
}): Promise<{ bereits: boolean }> => {
    const fassungId = (a?.fassungId || '').trim();
    const scan = (a?.scanDokumentId || '').trim();
    const signiert = (a?.signiertDokumentId || '').trim();
    if (!fassungId) throw new Error('Keine Fassung gewählt.');
    if (!scan || !signiert) throw new Error('Die eingelesene Datei liegt noch nicht in „Dokumente" — bitte erneut einlesen.');
    let antwort: { data: unknown; error: unknown };
    try {
        antwort = await supabase.rpc('vorank_papier_eingang', {
            p_fassung_id: fassungId,
            p_scan_id: scan,
            p_signiert_id: signiert,
            p_name: (a.name || '').replace(/\s+/g, ' ').trim() || null,
        });
    } catch (e) {
        return werfe(e);
    }
    if (antwort.error) return werfe(antwort.error);
    return bereitsAus(antwort.data);
};

/**
 * Hängt die unterschriebene PDF an eine digital unterschriebene Fassung
 * (RPC `vorank_signiert_setzen`, nur null → Wert). Ein zweites Fenster, das
 * schneller war: `{ bereits: true }` — dann gilt dessen Dokument, und der
 * Aufrufer verwirft sein eigenes. Wirft `Error` mit deutschem Klartext.
 */
export const signiertSetzen = async (fassungId: string, dokumentId: string): Promise<{ bereits: boolean }> => {
    const fid = (fassungId || '').trim();
    const did = (dokumentId || '').trim();
    if (!fid) throw new Error('Keine Fassung gewählt.');
    if (!did) throw new Error('Die unterschriebene PDF liegt noch nicht in „Dokumente".');
    let antwort: { data: unknown; error: unknown };
    try {
        antwort = await supabase.rpc('vorank_signiert_setzen', { p_fassung_id: fid, p_dokument_id: did });
    } catch (e) {
        return werfe(e);
    }
    if (antwort.error) return werfe(antwort.error);
    return bereitsAus(antwort.data);
};

// ─── Ablage in „Dokumente" ──────────────────────────────────────────────────

/**
 * Kennungen, die die bestehende Ablage der Vorankündigung schon vergibt
 * (`vorankFassungRef`: Versand `vorank:<archivId>`, Entwurf
 * `vorank-entwurf:<hash>`). Eine Unterschrift-Datei darf keine davon tragen:
 * die Doppelablage-Prüfung fände sonst das Versand- oder Entwurfsblatt und
 * gäbe dessen Id als „unterschriebene Fassung" zurück.
 */
const FREMDE_PRAEFIXE = ['vorank:', 'vorank-entwurf:'];

const DATEI_TYP_JE_ENDUNG: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
};
const ERLAUBTE_TYPEN = new Set(Object.values(DATEI_TYP_JE_ENDUNG));

/**
 * Der Inhaltstyp einer Vorankündigungs-Datei: PDF, JPEG oder PNG — sonst
 * `null`. Maßgeblich ist der gemeldete Typ; fehlt er (Blob aus Canvas oder
 * pdf-lib ohne Typ), entscheidet die Endung. Rein.
 */
export const vorankDateiTyp = (dateiname: string, gemeldet: string | null | undefined): string | null => {
    const typ = (gemeldet || '').trim().toLowerCase();
    if (typ && typ !== 'application/octet-stream') return ERLAUBTE_TYPEN.has(typ) ? typ : null;
    const endung = /\.([a-z0-9]{1,8})$/i.exec((dateiname || '').trim())?.[1]?.toLowerCase() || '';
    return DATEI_TYP_JE_ENDUNG[endung] || null;
};

const ablageKlartext = (e: unknown): string => {
    if (istFehlendeKennzeichnung(e)) {
        return 'Die Ablage kennt die Kennzeichnung erzeugter Unterlagen noch nicht (Migration Nr. 160/161 fehlt). '
            + 'Die Datei wurde nicht abgelegt — bitte an die Verwaltung wenden.';
    }
    if (e instanceof AblageFehler && e.code === '23514') {
        return 'Die Ablage kennt die Kategorie „Eigene Unterlagen" noch nicht (Migration Nr. 160/161 fehlt). '
            + 'Die Datei wurde nicht abgelegt — bitte an die Verwaltung wenden.';
    }
    if (istNetzFehler(e)) return NETZ_FEHLER_TEXT;
    return describeError(e);
};

/**
 * Der Ablage-Entwurf einer Unterschrift-Datei — rein. Kategorie und Ordner
 * wie jede Fassung der Vorankündigung (`eigene_unterlagen` /
 * „Vorankündigung"), `erzeugt_aus = 'vorankuendigung'` (im CHECK
 * `project_documents_erzeugt_aus_check` seit Nr. 160; dieselbe Kennung wie
 * Versand und Entwurf — die Unterscheidung trägt `fassung_ref`). Schlagwort
 * „unterschrieben" statt „versendet"/„Entwurf": kein Anlass des Kerns.
 */
export const vorankDateiEntwurf = (a: {
    titel: string;
    beschreibung: string;
    fassungRef: string;
    stand: string;
}): DokumentEntwurf => ({
    kategorie: EIGENE_UNTERLAGEN_KATEGORIE,
    ordner: ORDNER_JE_MODUL.vorankuendigung,
    titel: (a.titel || '').replace(/\s+/g, ' ').trim() || 'Vorankündigung — unterschrieben',
    beschreibung: (a.beschreibung || '').trim() || undefined,
    tags: ['unterschrieben'],
    module: ['vorankuendigung'],
    stand: (a.stand || '').trim() || undefined,
    nurAdmin: false,
    erzeugt_aus: 'vorankuendigung',
    fassung_ref: (a.fassungRef || '').trim(),
});

/**
 * Legt eine Datei der Unterschrift in „Dokumente" ab — die unterschriebene
 * PDF (digital oder normalisiert) oder das Original einer eingelesenen Datei
 * (PDF, JPG, PNG — ein Handyfoto bleibt, wie es kam). Liefert die Dokument-Id.
 *
 * Doppelablage-sicher über `fassungRef`: liegt die Kennung schon, wird nichts
 * hochgeladen und die vorhandene Id zurückgegeben; gewinnt ein zweites
 * Fenster den Wettlauf (23505), gilt dessen Zeile. Es entsteht höchstens EINE
 * NEUE Zeile — keine Ausgabe, kein Journal, keine Umbeschriftung (Dateikopf).
 * Die Kennung wählt der Aufrufer: fest je Fassung für die digital erzeugte
 * PDF (zwei Fenster → eine Zeile), je Eingabe verschieden für eingelesene
 * Dateien (eine bessere Datei bekäme sonst die Id der ersten zurück). Die
 * Präfixe der Versand-/Entwurfsablage (`vorank:`, `vorank-entwurf:`) sind
 * gesperrt. Wirft `Error` mit deutschem Klartext.
 */
export const legeVorankDateiAb = async (a: {
    projectId: string;
    datei: Blob;
    dateiname: string;
    titel: string;
    beschreibung: string;
    fassungRef: string;
    stand: string;
}): Promise<string> => {
    const pid = (a?.projectId || '').trim();
    if (!istGespeichertesProjekt(pid)) {
        throw new Error('Das Projekt muss erst gespeichert werden, bevor die Datei abgelegt werden kann.');
    }
    const ref = (a.fassungRef || '').trim();
    if (!ref) throw new Error('Der Datei fehlt die Fassungskennung — sie wurde nicht abgelegt.');
    if (FREMDE_PRAEFIXE.some(p => ref.startsWith(p))) {
        throw new Error(`Die Kennung „${ref}" gehört der Versand-/Entwurfsablage — die Datei wurde nicht abgelegt.`);
    }
    const datei = a.datei;
    if (!datei || typeof datei.size !== 'number' || !(datei.size > 0)) throw new Error('Die Datei ist leer.');
    const dateiname = (a.dateiname || '').trim();
    const typ = vorankDateiTyp(dateiname, datei.type);
    if (!typ) throw new Error(`„${dateiname || 'Datei'}" ist kein PDF, JPG oder PNG — sie wurde nicht abgelegt.`);
    const absage = pruefeDatei({ name: dateiname, size: datei.size, type: typ });
    if (absage) throw new Error(absage);
    // Ohne Typ schickte storage-js `text/plain`, das der Bucket ablehnt.
    const mitTyp = datei.type === typ ? datei : datei.slice(0, datei.size, typ);
    const entwurf = vorankDateiEntwurf({ titel: a.titel, beschreibung: a.beschreibung, fassungRef: ref, stand: a.stand });

    try {
        const vorhanden = await findeErzeugteUnterlage(pid, 'vorankuendigung', ref);
        if (vorhanden?.id) return vorhanden.id;
        const wer = await angemeldeterBenutzer();
        const dokument = await uploadDokument({
            projectId: pid,
            datei: mitTyp,
            dateiname,
            entwurf,
            benutzerId: wer.id,
            benutzerName: wer.name,
        });
        if (!dokument?.id) throw new Error('Die Ablage hat keine Dokument-Id zurückgegeben.');
        return dokument.id;
    } catch (e) {
        // Liegt die eigene Datei nach dem Zeilenfehler noch im Bucket: ein
        // zweiter Anlauf, sonst wenigstens ins Protokoll.
        const stand = e instanceof AblageFehler ? e.rueckbau : null;
        if (stand?.verwaist && !(await entferneDatei(stand.pfad))) {
            console.warn('Verwaiste Datei in der Ablage (Vorankündigung):', stand.pfad);
        }
        if (istDoppelablage(e)) {
            const gewinner = await findeErzeugteUnterlage(pid, 'vorankuendigung', ref).catch(() => null);
            if (gewinner?.id) return gewinner.id;
            throw new Error('Diese Datei liegt bereits in der Ablage, ließ sich aber gerade nicht wiederfinden. Bitte neu laden.');
        }
        throw new Error(ablageKlartext(e));
    }
};

/**
 * Die Bytes eines Eintrags in „Dokumente" — für Plot und Download der
 * unterschriebenen PDF. Der Pfad kommt aus der Zeile; ist sie nicht lesbar
 * (gelöscht, Admin-Schloss, fremdes Projekt), sagt der Fehler das. Wirft
 * `Error` mit deutschem Klartext (`ladeDokumentHerunter` liefert ihn für den
 * Speicher selbst).
 */
export const ladeDokumentBytes = async (dokumentId: string): Promise<ArrayBuffer> => {
    const id = (dokumentId || '').trim();
    if (!id) throw new Error('Zu dieser Fassung ist keine Datei hinterlegt.');
    let antwort: { data: unknown; error: unknown };
    try {
        antwort = await supabase.from(T_DOKUMENTE).select('id, storage_pfad').eq('id', id).maybeSingle();
    } catch (e) {
        throw new Error(istNetzFehler(e) ? NETZ_FEHLER_TEXT : `Die Datei ließ sich nicht finden (${describeError(e)}).`);
    }
    if (antwort.error) {
        const e = antwort.error;
        if (istFehlendesSchema(e)) throw new Error('Die Dokumentenablage ist auf dieser Umgebung nicht eingerichtet.');
        throw new Error(istNetzFehler(e) ? NETZ_FEHLER_TEXT : `Die Datei ließ sich nicht finden (${describeError(e)}).`);
    }
    const pfad = strOrNull(alsObjekt(antwort.data)?.storage_pfad);
    if (!pfad) {
        throw new Error('Die Datei ist in „Dokumente" nicht (mehr) vorhanden — gelöscht, gesperrt oder ohne Berechtigung.');
    }
    const blob = await ladeDokumentHerunter(pfad);
    return blob.arrayBuffer();
};
